import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { chunkRatingScaleValues } from '../../../lib/rating-display'
import type { SurveyQuestion } from '../../../lib/survey-question.types'
import { SurveyRatingStarIcon } from '../SurveyRatingStarIcon'
import {
  surveyRatingNumberButtonSelectedSx,
  surveyRatingNumberButtonSx,
  surveyRatingNumbersRowSx,
  surveyRatingNumbersRowsStackSx,
  surveyRatingRootSx,
  surveyRatingScaleLabelEndSx,
  surveyRatingScaleLabelStartSx,
  surveyRatingScaleLabelsRowSx,
  surveyRatingStarButtonSx,
  surveyRatingStarsRowSx,
  surveyRatingStarsRowsStackSx,
  surveyRatingValueCaptionSx,
} from '../../../styles/surveyExecutionStyles'

interface RatingQuestionProps {
  readonly question: SurveyQuestion
  readonly value: number | null
  readonly onChange: (rating: number) => void
}

export function RatingQuestion({ question, value, onChange }: RatingQuestionProps) {
  const max = question.ratingScaleMax
  const low = question.ratingLowLabel.trim()
  const high = question.ratingHighLabel.trim()
  const isStars = question.ratingDisplayStyle === 'stars'
  const rows = chunkRatingScaleValues(max)

  return (
    <Box sx={surveyRatingRootSx} role="radiogroup" aria-label={question.prompt}>
      {isStars ? (
        <Box
          sx={surveyRatingStarsRowsStackSx}
          data-testid={`survey-q-${question.id}-rating-stars`}
        >
          {rows.map((rowValues, rowIndex) => (
            <Box
              key={`stars-${rowValues.join('-')}`}
              sx={surveyRatingStarsRowSx}
              data-testid={`survey-q-${question.id}-rating-stars-row-${rowIndex}`}
            >
              {rowValues.map((rating) => {
                const filled = value != null && rating <= value
                return (
                  <Box
                    key={rating}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={value === rating}
                    aria-label={`Rate ${rating} of ${max}`}
                    data-testid={`survey-q-${question.id}-rating-${rating}`}
                    onClick={() => onChange(rating)}
                    sx={surveyRatingStarButtonSx}
                  >
                    <SurveyRatingStarIcon
                      filled={filled}
                      aria-hidden
                      data-testid={`survey-q-${question.id}-rating-star-icon-${rating}`}
                    />
                  </Box>
                )
              })}
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={surveyRatingNumbersRowsStackSx}
          data-testid={`survey-q-${question.id}-rating-numbers`}
        >
          {rows.map((rowValues, rowIndex) => (
            <Box
              key={`numbers-${rowValues.join('-')}`}
              sx={surveyRatingNumbersRowSx}
              data-testid={`survey-q-${question.id}-rating-numbers-row-${rowIndex}`}
            >
              {rowValues.map((rating) => {
                const selected = value === rating
                return (
                  <Box
                    key={rating}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={`Select ${rating} of ${max}`}
                    data-testid={`survey-q-${question.id}-rating-${rating}`}
                    onClick={() => onChange(rating)}
                    sx={{
                      ...surveyRatingNumberButtonSx,
                      ...(selected ? surveyRatingNumberButtonSelectedSx : {}),
                    }}
                  >
                    {rating}
                  </Box>
                )
              })}
            </Box>
          ))}
        </Box>
      )}

      {low || high ? (
        <Box sx={surveyRatingScaleLabelsRowSx} data-testid={`survey-q-${question.id}-rating-labels`}>
          {low ? (
            <Typography component="span" sx={surveyRatingScaleLabelStartSx}>
              {low}
            </Typography>
          ) : (
            <span />
          )}
          {high ? (
            <Typography component="span" sx={surveyRatingScaleLabelEndSx}>
              {high}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {value == null ? null : (
        <Typography
          component="p"
          sx={surveyRatingValueCaptionSx}
          data-testid={`survey-q-${question.id}-rating-caption`}
        >
          {value} out of {max}
        </Typography>
      )}
    </Box>
  )
}
